import * as amplitude from "@amplitude/analytics-browser";
import * as Sentry from "@sentry/react";
import { config } from "@/config";
import { AnalyticsDestination } from "@/models/analytics";
import { EventName } from "./events";

// The single Amplitude entry point for the frontend (MS-3429). Never import
// `@amplitude/analytics-browser` anywhere else — all feature code goes through
// `trackEvent()`. A tracking failure must never surface to the user: every
// public function catches and reports to Sentry instead of throwing (except
// the naming asserts, which throw in dev builds by design).

// Events fired before the flag resolves are held, not dropped. The window is
// short but it covers the first pageview of every session, which is where a
// funnel starts. Bounded so a gate that never resolves cannot grow unbounded.
const MAX_BUFFERED_EVENTS = 50;

// `sessionStorage["dev:metrics:logging"] = "true"` → after refresh, events log
// to the console and nothing is sent to Amplitude. Lets QE verify events fire
// without an Amplitude seat.
const METRICS_DEBUG_KEY = "dev:metrics:logging";

// Needed to accommodate cases that are not strictly camelCase, e.g. 'currentURL'
const CAMEL_CASE_KEY = /^[a-z][a-zA-Z0-9]*$/;

export interface RouteContext {
  product: "Midship";
  module: string;
  viewLocation: string;
  currentURL: string;
}

// The client picks a key by the name it was given; it never works out which
// project applies. See `config.ts`.
const WRITE_KEYS: Record<AnalyticsDestination, string> = {
  prod: config.amplitudeApiKeyProd,
  qa: config.amplitudeApiKeyQa,
};

type GateState = "pending" | "enabled" | "disabled";

interface BufferedEvent {
  name: string;
  properties: Record<string, unknown>;
  capturedWorkspace: boolean;
}

interface PendingIdentity {
  userId: string;
  accountId: string | null;
  // True for a composed Optro id, whose profile the Optro monolith also writes.
  sharesOptroProfile: boolean;
}

type WorkspaceProperties = Record<string, string | number | boolean>;

// Amplitude's Accounts group type, shared with the backend and Optro.
const ACCOUNT_GROUP_TYPE = "account";

// Sent on every event, never as user properties: one user can act in several
// workspaces. Reserved so feature code cannot pass them.
const WORKSPACE_EVENT_PROPERTIES: readonly string[] = [
  "workspaceId",
  "roleId",
  "subscriptionStatus",
  "accountId",
  "baseRole",
  "workspaceType",
  "authProvider",
];

// The Optro monolith sets these on the same composed ids, so they are cleared
// only on a Midship-only id, where no monolith value exists to lose.
const OPTRO_SHARED_PROPERTIES: readonly string[] = [
  "roleId",
  "baseRole",
  "accountId",
];

// One client per project rather than the default instance, because a key is
// fixed at `init` and a workspace can move between projects within a session.
let client: amplitude.Types.BrowserClient | null = null;
let activeDestination: AnalyticsDestination | null = null;
let routeContext: RouteContext | null = null;
let workspaceContext: WorkspaceProperties = {};
let gate: GateState = "pending";
let bufferedEvents: BufferedEvent[] = [];
let pendingIdentity: PendingIdentity | null = null;
// Survives a client swap: the identity already sent to the old project has to
// be re-sent to the new one, or every event after a switch is anonymous.
let appliedIdentity: PendingIdentity | null = null;
// A deployment missing a key never sets `activeDestination`, so every gate run
// would report the same fault again. Latched per project, like the backend's
// `report_unprovisioned`.
const reportedMissingKeys = new Set<AnalyticsDestination>();
// A bad key or a blocked domain fails every event, so each status code is
// reported once per project per page session, like the backend's callback.
const reportedDeliveryFailures = new Set<string>();
// The SDK's `OPT_OUT_MESSAGE`, which neither Amplitude package exports.
const SDK_OPT_OUT_MESSAGE = "Event skipped due to optOut config";

function isDebugEnabled(): boolean {
  try {
    return sessionStorage.getItem(METRICS_DEBUG_KEY) === "true";
  } catch {
    return false;
  }
}

// Naming violations should fail loudly while you are working and never reach a
// user, so the asserts throw here and correct silently in deployed builds.
function throwsOnViolation(): boolean {
  return import.meta.env.DEV;
}

function setSdkOptOut(optOut: boolean): void {
  try {
    if (!client) return;
    client.setOptOut(optOut);
  } catch (error) {
    reportMetricsError(error, "setOptOut");
  }
}

function reportMetricsError(
  error: unknown,
  operation: string,
  extra?: Record<string, unknown>,
): void {
  Sentry.captureException(error, {
    tags: { source: "metrics", operation },
    extra,
  });
}

// Only the opt-out skip (debug mode, closed gate) is ignored. Code 0 also
// carries real failures: a throwing plugin, an uninitialised client, a null
// transport response. The SDK message stays out of the Error message so Sentry
// groups one issue per cause.
function reportDeliveryFailure(
  destination: AnalyticsDestination | null,
  result: amplitude.Types.Result,
): void {
  if (result.code === 200 || result.message === SDK_OPT_OUT_MESSAGE) return;
  const key = `${destination}:${result.code}`;
  if (reportedDeliveryFailures.has(key)) return;
  reportedDeliveryFailures.add(key);

  const error = new Error(
    `Amplitude delivery failed: ${destination} ${result.code}`,
  );
  if (import.meta.env.DEV) {
    console.error(`[metrics] ${error.message}`, result.message);
  }
  reportMetricsError(error, "delivery", { sdkMessage: result.message });
}

function midshipContextPlugin(): amplitude.Types.EnrichmentPlugin {
  return {
    name: "midship-context",
    type: "enrichment",
    execute: (event) => {
      event.event_properties = {
        ...event.event_properties,
        eventSource: "midship-frontend",
        environment: config.environment,
      };
      return Promise.resolve(event);
    },
  };
}

function assertEventName(name: string): string {
  if (name === name.toLowerCase()) return name;
  if (throwsOnViolation()) {
    throw new Error(`[metrics] event names must be lowercase: "${name}"`);
  }
  return name.toLowerCase();
}

function assertPropertyKeys(properties: Record<string, unknown>): void {
  if (!throwsOnViolation()) return;
  const bad = Object.keys(properties).filter((k) => !CAMEL_CASE_KEY.test(k));
  if (bad.length > 0) {
    throw new Error(
      `[metrics] property keys must be camelCase: ${bad.join(", ")}`,
    );
  }
  const reserved = Object.keys(properties).filter((k) =>
    WORKSPACE_EVENT_PROPERTIES.includes(k),
  );
  if (reserved.length > 0) {
    throw new Error(
      `[metrics] property keys are reserved for workspace context: ${reserved.join(", ")}`,
    );
  }
}

/**
 * Point the module at the Amplitude project the server named.
 *
 * Called by `MetricsGate` on login and workspace switch. A change of project
 * means a new client: the key cannot be swapped on a live one, and the two
 * projects share no user or session identity anyway, so the old client is
 * flushed and dropped rather than migrated.
 */
export function setMetricsDestination(
  destination: AnalyticsDestination | null,
): boolean {
  try {
    if (destination === null) return false;
    if (destination === activeDestination) return client !== null;

    const apiKey = WRITE_KEYS[destination];
    if (!apiKey) {
      if (!reportedMissingKeys.has(destination)) {
        reportedMissingKeys.add(destination);
        reportMetricsError(
          new Error(`No Amplitude write key for destination "${destination}"`),
          "setDestination",
        );
      }
      return false;
    }

    // Flushed but not reset: the outgoing client's queued events were built
    // with their identity at track time and belong in the project they were
    // fired at. It is then dropped rather than reused.
    client?.flush();

    const next = amplitude.createInstance();
    // SDK bug: a plugin added after init() silently never runs — register the
    // enrichment plugin first. Each instance needs its own.
    next.add(midshipContextPlugin());
    // Sessions and attribution are the SDK's to own. `pageViews` stays off —
    // the module fires its own `pageview` with masked URLs and route context,
    // where the SDK's would double-count and send raw URLs. The interaction,
    // form and file-download captures attach user-entered text, file names and
    // link URLs, so they stay off for PII reasons.
    // `init` cannot throw synchronously; every failure (storage denied, remote
    // config unreachable) arrives as a rejected promise, and an SDK that never
    // becomes ready queues `track` calls for the page lifetime. Dropping the
    // client on rejection lets the next gate run rebuild it, and keeps the
    // fault out of the global unhandled-rejection bucket.
    next
      .init(apiKey, {
        // Explicit, so the SDK cannot fall back to its own cookie — a single
        // past debug session would otherwise silence the module for good. The
        // gate decides when this opens, so a client created before the flag
        // resolves still sends nothing.
        optOut: gate !== "enabled" || isDebugEnabled(),
        // The SDK's native version field, so it lands in Amplitude's standard
        // "Version" column rather than costing a custom property.
        ...(config.appVersion ? { appVersion: config.appVersion } : {}),
        autocapture: {
          sessions: true,
          attribution: true,
          pageViews: false,
          elementInteractions: false,
          formInteractions: false,
          fileDownloads: false,
        },
      })
      .promise.catch((error: unknown) => {
        reportMetricsError(error, "init");
        if (client === next) {
          client = null;
          activeDestination = null;
        }
      });
    client = next;
    activeDestination = destination;

    // A client created after the gate opened has missed the identity, whether
    // it is still held or was already sent to the project we just left.
    const identity = pendingIdentity ?? appliedIdentity;
    if (gate === "enabled" && identity) {
      pendingIdentity = null;
      applyIdentity(identity);
    }
    return true;
  } catch (error) {
    reportMetricsError(error, "setDestination");
    return false;
  }
}

/** Route context auto-appended to every event; kept current by MetricsPageviews. */
export function setRouteContext(context: RouteContext): void {
  routeContext = context;
}

/**
 * Open or close the LaunchDarkly gate, once flag values are authoritative for
 * the signed-in workspace. Callers pass `false` when no client is live, so an
 * open gate always has somewhere to send.
 */
export function setMetricsGate(enabled: boolean): void {
  gate = enabled ? "enabled" : "disabled";
  setSdkOptOut(!enabled || isDebugEnabled());

  const pendingEvents = bufferedEvents;
  bufferedEvents = [];
  // The held identity deliberately survives a closed gate. LaunchDarkly pushes
  // flag changes into open tabs, so a workspace can enter the rollout
  // mid-session — and `MetricsIdentify` will not fire again for a user who is
  // already signed in. Dropping it here would leave every later event
  // anonymous. Only logout clears it.
  if (!enabled) return;

  // `appliedIdentity` covers a client swapped in while the gate was closed: the
  // identity was already sent to the old project and never held as pending.
  const identity = pendingIdentity ?? appliedIdentity;
  pendingIdentity = null;
  if (identity) {
    applyIdentity(identity);
  }
  // The first pageview after a page load fires before MetricsIdentify runs. A
  // switch reloads the page and logout empties the buffer, so an event that
  // captured no workspace belongs to the one identified since.
  for (const event of pendingEvents) {
    send(
      event.name,
      event.capturedWorkspace
        ? event.properties
        : { ...event.properties, ...workspaceContext },
    );
  }
}

function send(eventName: string, props: Record<string, unknown>): void {
  try {
    if (!client) return;
    // The destination is read now because a workspace switch can change it
    // before delivery settles.
    const destination = activeDestination;
    client
      .track(eventName, props)
      .promise.then((result) => reportDeliveryFailure(destination, result))
      .catch((error: unknown) => reportMetricsError(error, "delivery"));
  } catch (error) {
    reportMetricsError(error, "trackEvent");
  }
}

/**
 * Record a pageview. Owns the page-scoped properties — the ones that describe
 * the visit rather than an action within it — so they never end up on every
 * event. Called by `MetricsPageviews`, not by feature code.
 */
export function pageView(): void {
  emit(EventName.Pageview);
}

/** The entry point feature code uses to record a user action. */
export function trackEvent(
  name: EventName,
  properties: Record<string, unknown> = {},
): void {
  emit(name, properties);
}

function emit(name: EventName, properties: Record<string, unknown> = {}): void {
  const eventName = assertEventName(name);
  assertPropertyKeys(properties);

  // Context is captured now, not at flush time: a buffered event must carry the
  // route and workspace it happened in, not whatever is current when the gate
  // resolves. `actorType` is always `user`: a browser event is by definition a
  // person acting. An event property rather than a user property because the
  // platform sends it per request (auditboard-backend `metrics.ts`). Workspace
  // context and `actorType` go last, so feature code cannot override them.
  const props = {
    ...routeContext,
    ...properties,
    ...workspaceContext,
    actorType: "user",
  };

  // Logged where the event is fired, not where it is delivered, so debug mode
  // still shows the event when the gate is closed or has yet to resolve —
  // otherwise there is no way to verify events locally.
  if (isDebugEnabled()) {
    console.log(`[metrics] "${eventName}" (gate: ${gate})`, props);
  }

  if (gate === "disabled") return;
  if (gate === "pending") {
    if (bufferedEvents.length < MAX_BUFFERED_EVENTS) {
      bufferedEvents.push({
        name: eventName,
        properties: props,
        capturedWorkspace: Object.keys(workspaceContext).length > 0,
      });
    }
    return;
  }
  send(eventName, props);
}

/**
 * Set the Amplitude user and `account` group for the session, and the
 * workspace properties attached to every later event. Called by
 * MetricsIdentify at login and on a workspace switch, never per event.
 */
export function identify(
  userId: string,
  workspaceProperties: WorkspaceProperties,
  accountId: string | null,
  sharesOptroProfile: boolean,
): void {
  // Set before the gate check so events buffered while it is pending carry it.
  workspaceContext = Object.fromEntries(
    Object.entries(workspaceProperties).filter(
      ([key, value]) =>
        WORKSPACE_EVENT_PROPERTIES.includes(key) && value !== "",
    ),
  );

  if (isDebugEnabled()) {
    // Deliberately not logging the payload — it drowns out the events QE is
    // trying to verify.
    console.log(`[metrics] identify (gate: ${gate}, payload not logged)`);
  }

  if (gate !== "enabled") {
    // Held rather than dropped, whether the gate is pending or closed, and
    // applied before any buffered events flush so they are attributed to the
    // user rather than to an anonymous device.
    pendingIdentity = { userId, accountId, sharesOptroProfile };
    return;
  }
  applyIdentity({ userId, accountId, sharesOptroProfile });
}

function applyIdentity(identity: PendingIdentity): void {
  try {
    if (!client) return;

    client.setUserId(identity.userId);
    const identifyObject = new amplitude.Identify();
    // Earlier builds set these as user properties, which stick to the profile
    // until unset.
    for (const key of WORKSPACE_EVENT_PROPERTIES) {
      if (
        identity.sharesOptroProfile &&
        OPTRO_SHARED_PROPERTIES.includes(key)
      ) {
        continue;
      }
      identifyObject.unset(key);
    }
    if (!identity.accountId) {
      // Group membership is itself a user property, so an unmapped workspace
      // clears the previous account rather than keeping it.
      identifyObject.unset(ACCOUNT_GROUP_TYPE);
    }
    client.identify(identifyObject);
    if (identity.accountId) {
      client.setGroup(ACCOUNT_GROUP_TYPE, identity.accountId);
    }
    appliedIdentity = identity;
  } catch (error) {
    reportMetricsError(error, "identify");
  }
}

/**
 * Clear the Amplitude identity on logout so the next login re-identifies
 * cleanly. The gate returns to `pending` because the flag is per workspace and
 * the next session may be a different one; anything still buffered belonged to
 * the session that just ended.
 */
export function resetMetrics(): void {
  gate = "pending";
  bufferedEvents = [];
  pendingIdentity = null;
  appliedIdentity = null;
  workspaceContext = {};
  activeDestination = null;
  setSdkOptOut(true);

  try {
    if (!client) return;
    client.reset();
  } catch (error) {
    reportMetricsError(error, "reset");
  }
}

/**
 * The SDK's current session id, for propagation to the backend.
 *
 * Null whenever no browser session exists — the SDK is inert, or the gate is
 * not open — so callers omit the header rather than send a placeholder
 * Amplitude would read as a real session.
 */
export function getSessionId(): number | null {
  try {
    if (!client || gate !== "enabled") return null;
    return client.getSessionId() ?? null;
  } catch (error) {
    reportMetricsError(error, "getSessionId");
    return null;
  }
}
